import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { syncAccessTokenFromCredentials, clearAccessToken } from '@/lib/authAccessToken';
import { setBookingAdminMutationFn, setBookingAdminQueryJsonFetch } from '@/lib/queryClient';
import { sessionHasBeenCleared } from '../helpers/helpers';
import AuthenticatingSpinner from '../components/AuthenticatingSpinner';

export interface ErrorObject extends Error {
  errors: unknown;
  code: number;
}

type Credentials = {
  access_token: string;
};

const getError = (code: number, errors: unknown = {}) => {
  const errorObject: ErrorObject = {
    name: 'Error',
    message: 'Error in API call',
    errors,
    code,
  };

  return errorObject;
};

export const apiFetch = async (
  url: string,
  body = {},
  method = 'GET',
  userHeaders: HeadersInit = {},
  credentials: RequestCredentials = undefined,
  plain = false,
  formDataBody: FormData | undefined = undefined
) => {
  const options: RequestInit = {};

  options.headers = {
    ...(!formDataBody && {
      'Content-Type': 'application/json',
    }),
    ...userHeaders,
  };

  const METHODS_WITHOUT_BODY = ['GET', 'DELETE'];
  if (!METHODS_WITHOUT_BODY.includes(method)) {
    options.body = formDataBody || JSON.stringify(body);
  }

  options.method = method;
  options.credentials = credentials;

  return fetch(url, {
    ...options,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }).then(async (response): Promise<any> => {
    if (response.ok && plain) {
      return response.text();
    }
    if (response.ok) {
      return response
        .json()
        .catch(() => response)
        .then((responseData) => {
          if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
            return responseData;
          }
          // Booking API `PagedListResponse`: preserve `metadata` and `_links` with `data`.
          if ('metadata' in responseData || '_links' in responseData) {
            return responseData;
          }
          const keys = Object.keys(responseData as Record<string, unknown>);
          if (keys.length === 1 && keys[0] === 'data') {
            return (responseData as { data: unknown }).data;
          }
          return responseData;
        });
    }

    return response
      .json()
      .catch(() => {
        throw getError(response.status, response.statusText);
      })
      .then((error) => {
        throw getError(Number(error.code || response.status), error.errors || error);
      });
  });
};

/** Same-origin `/tokens` and `/refresh` with cookies (eposnow.myaccount.ui `ApiContext`). */
export const apiFetchToSSR = (url: string, body: unknown = {}, method = 'GET') =>
  apiFetch(url, body, method, { 'Access-Control-Allow-Credentials': 'true' }, 'include');

const logoutFnDefault = () => {
  localStorage.clear();
  sessionStorage.clear();

  window.location.assign('/logout');
};

export type ApiContextValue = {
  fetchIsReady: boolean;
  /** Authenticated fetch (Bearer); return shape depends on endpoint. Same signature as myaccount (`plain` before `extraHeaders`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiFetch: (
    url: string,
    body?: unknown,
    method?: string,
    plain?: boolean,
    extraHeaders?: HeadersInit,
    formDataBody?: FormData
  ) => Promise<any>;
  logout: () => void;
  getAccessToken: () => string | null;
};

const ApiContext = React.createContext<ApiContextValue | undefined>(undefined);

const refreshTokenListeners: EventListenerOrEventListenerObject[] = [];
let isRefreshingToken = false;
let accessToken: string | undefined;

function removeAllRefreshTokenListeners() {
  while (refreshTokenListeners.length > 0) {
    const listener = refreshTokenListeners.pop();
    document.removeEventListener('token-refresh', listener);
  }
}

function notifyRefreshedToken(token: Credentials | undefined) {
  isRefreshingToken = false;

  const tokenRefreshEvent = new CustomEvent('token-refresh', { detail: token });
  document.dispatchEvent(tokenRefreshEvent);

  removeAllRefreshTokenListeners();
}

const ApiContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [credentials, setCredentials] = useState<Credentials | undefined>(undefined);
  const [fetchIsReady, setFetchIsReady] = useState<boolean>(false);
  const [successfulAuthorization, setSuccessfulAuthorization] = useState<boolean>(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (credentials === undefined) {
      timer = setTimeout(() => {
        if (credentials === undefined) {
          window.location.href = '/';
        }
      }, 3000);
      return () => clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [credentials]);

  const updateCredentials = useCallback((token: Credentials | undefined) => {
    setCredentials(token);
    accessToken = token?.access_token;
    syncAccessTokenFromCredentials(token ?? null);
  }, []);

  useEffect(() => () => {
      clearAccessToken();
      accessToken = undefined;
    }, []);

  useEffect(() => {
    if (credentials === null || credentials === undefined) {
      apiFetch(
        '/tokens',
        {},
        'GET',
        { 'Access-Control-Allow-Credentials': 'true' },
        'include'
      ).then((token: Credentials) => {
        updateCredentials(token);
        setFetchIsReady(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- myaccount: run once on mount
  }, []);

  useEffect(() => {
    if (fetchIsReady) {
      setSuccessfulAuthorization(true);
    }
  }, [fetchIsReady]);

  const refreshToken = useCallback(async () => {
    if (isRefreshingToken) {
      return new Promise<Credentials>((resolve, reject) => {
        const refreshTokenListener = (event: Event) => {
          const e = event as CustomEvent<Credentials | undefined>;
          const newCredentials = e.detail;
          if (newCredentials) {
            resolve(newCredentials);
          } else {
            reject();
          }
        };
        document.addEventListener('token-refresh', refreshTokenListener);

        refreshTokenListeners.push(refreshTokenListener);
      });
    }

    isRefreshingToken = true;

    return apiFetch(
      '/refresh',
      {},
      'GET',
      { 'Access-Control-Allow-Credentials': 'true' },
      'include'
    )
      .then((token: Credentials) => {
        updateCredentials(token);
        notifyRefreshedToken(token);
        return token;
      })
      .catch((_refreshError) => {
        notifyRefreshedToken(undefined);
        throw getError(401, 'Problem while refreshing token');
      });
  }, [updateCredentials]);

  const apiFetchUsingAuth = useCallback(
    async (
      url: string,
      body: unknown = {},
      method = 'GET',
      plain = false,
      extraHeaders: HeadersInit = {},
      formDataBody: FormData | undefined = undefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Promise<any> => {
      if (!fetchIsReady) {
        throw getError(
          401,
          'Fetch is not ready, please be sure to enable queries using supplied fetchIsReady variable.'
        );
      }

      if (sessionHasBeenCleared(url)) {
        updateCredentials(undefined);
        throw getError(401, 'Session has been cleared.');
      }

      const headers = {
        Authorization: `Bearer ${credentials?.access_token}`,
        ...extraHeaders,
      };

      try {
        return await apiFetch(url, body, method, headers, undefined, plain, formDataBody);
      } catch (error: unknown) {
        const err = error as ErrorObject;
        if (err.code === 401) {
          try {
            if (credentials?.access_token === accessToken) {
              await refreshToken().catch((_refreshError) => {
                throw getError(401, 'Problem while refreshing token');
              });
            }

            headers.Authorization = `Bearer ${accessToken}`;

            return await apiFetch(url, body, method, headers, undefined, plain, formDataBody);
          } catch (retryError: unknown) {
            const re = retryError as ErrorObject;
            if (re.code === 401) {
              logoutFnDefault();
            }

            throw retryError;
          }
        }

        throw error;
      }
    },
    [refreshToken, fetchIsReady, credentials, updateCredentials]
  );

  useLayoutEffect(() => {
    const getter = (resolvedUrl: string) => apiFetchUsingAuth(resolvedUrl, {}, 'GET', false);
    setBookingAdminQueryJsonFetch(getter);
    const mutator: (method: string, resolvedUrl: string, data?: unknown) => Promise<unknown> = (
      method,
      resolvedUrl,
      body,
    ) => apiFetchUsingAuth(resolvedUrl, body === undefined ? {} : body, method, false);
    setBookingAdminMutationFn(mutator);
    return () => {
      setBookingAdminQueryJsonFetch(null);
      setBookingAdminMutationFn(null);
    };
  }, [apiFetchUsingAuth]);

  const logout = useCallback(() => {
    updateCredentials(undefined);
    setFetchIsReady(false);

    logoutFnDefault();
  }, [updateCredentials]);

  const getAccessToken = useCallback(() => credentials?.access_token || null, [credentials]);

  if (!fetchIsReady && !successfulAuthorization) {
    return <AuthenticatingSpinner />;
  }

  if (successfulAuthorization && !fetchIsReady) {
    return null;
  }

  return (
    <ApiContext.Provider
      value={{
        fetchIsReady,
        apiFetch: apiFetchUsingAuth,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export function useApiContext(): ApiContextValue {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApiContext must be used within ApiContextProvider');
  }
  return ctx;
}

export { ApiContext, ApiContextProvider };
